// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Contract Review Documentation Dialog
 *
 * Explains how contract review analysis works and how to use the results.
 */
import type { FC } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Button,
  ScrollArea,
} from '@/components/ui'
import {
  BookOpen,
  FileText,
  Download,
  AlertTriangle,
  Workflow,
  Database,
  GitCompare,
  Upload,
} from 'lucide-react'

export const DocsDialog: FC = () => {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1" data-testid="cr-docs-button">
          <BookOpen className="h-4 w-4" />
          Docs
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[85vh]" data-testid="cr-docs-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <BookOpen className="h-5 w-5" />
            Contract Review
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="h-[65vh] pr-4">
          <div className="space-y-8 text-sm">
            {/* Overview */}
            <section>
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-500" />
                Overview
              </h2>
              <p className="text-muted-foreground mb-3">
                Contract Review helps reviewers identify terms, clauses, and potential issues
                in contracts before approval. AI analysis highlights findings directly in the
                document for human review.
              </p>
              <p className="text-muted-foreground">
                The AI augments—but does not replace—human judgment. Reviewers examine AI
                findings and make final approval decisions.
              </p>
            </section>

            {/* Uploading Documents */}
            <section>
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Upload className="h-5 w-5 text-indigo-500" />
                Uploading Documents
              </h2>
              <p className="text-muted-foreground mb-3">
                Drag and drop files or click to browse. Supported formats: PDF, DOCX, TXT.
              </p>
              <div className="bg-muted/50 rounded-lg p-4 space-y-2 text-xs">
                <p className="text-muted-foreground">
                  <strong className="text-foreground">Auto ID:</strong> Each document gets
                  an auto-assigned ID (ORA-1, ORA-2) for tracking.
                </p>
                <p className="text-muted-foreground">
                  <strong className="text-foreground">Versions:</strong> Upload new versions
                  of existing documents via the version manager.
                </p>
              </div>
            </section>

            {/* How Analysis Works */}
            <section>
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Workflow className="h-5 w-5 text-purple-500" />
                How Analysis Works
              </h2>
              <div className="space-y-4">
                <Step
                  number={1}
                  title="Text Extraction"
                  description="Readable text is extracted from your document."
                />
                <Step
                  number={2}
                  title="AI Analysis"
                  description="AI scans the document against configured Knowledge Base guidance items."
                />
                <Step
                  number={3}
                  title="Findings Generation"
                  description="Issues are flagged with severity, location, and recommended changes."
                />
                <Step
                  number={4}
                  title="Human Review"
                  description="Review findings, download annotated documents, and make approval decisions."
                />
              </div>
            </section>

            {/* Understanding Findings */}
            <section>
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                Understanding Findings
              </h2>
              <p className="text-muted-foreground mb-4">
                Each finding shows risk level and recommended action:
              </p>
              <div className="space-y-4">
                <div className="bg-muted/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-0.5 rounded text-xs font-semibold bg-red-500/20 text-red-600 border border-red-500/30">HIGH</span>
                    <span className="px-2 py-0.5 rounded text-xs font-semibold bg-amber-500/20 text-amber-600 border border-amber-500/30">MEDIUM</span>
                    <span className="px-2 py-0.5 rounded text-xs font-semibold bg-green-500/20 text-green-600 border border-green-500/30">LOW</span>
                  </div>
                  <p className="text-muted-foreground text-xs">
                    <strong>Severity</strong> indicates institutional risk. High-severity findings
                    typically require negotiation or legal review.
                  </p>
                </div>
                <div className="bg-muted/50 rounded-lg p-4">
                  <p className="font-medium text-sm mb-2">Finding Types</p>
                  <div className="space-y-1 text-xs text-muted-foreground">
                    <p>• <strong>Delete</strong> — Remove the flagged phrase</p>
                    <p>• <strong>Replace</strong> — Substitute with recommended text</p>
                    <p>• <strong>Review</strong> — Examine clause for potential issues</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Downloading Results */}
            <section>
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Download className="h-5 w-5 text-green-500" />
                Downloading Results
              </h2>
              <p className="text-muted-foreground mb-3">
                After analysis completes, download annotated documents:
              </p>
              <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                <div className="flex gap-2">
                  <span className="font-medium text-foreground w-24 shrink-0">PDF</span>
                  <span className="text-muted-foreground text-xs">
                    Highlights and popup comments. Best viewed in Adobe Acrobat.
                  </span>
                </div>
                <div className="flex gap-2">
                  <span className="font-medium text-foreground w-24 shrink-0">DOCX</span>
                  <span className="text-muted-foreground text-xs">
                    Word comments in the original document (DOCX uploads only).
                  </span>
                </div>
              </div>
            </section>

            {/* Version Comparison */}
            <section>
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <GitCompare className="h-5 w-5 text-cyan-500" />
                Version Comparison
              </h2>
              <p className="text-muted-foreground mb-3">
                Compare two versions of a document to identify changes between contract iterations.
              </p>
              <div className="bg-muted/50 rounded-lg p-4 space-y-2 text-xs">
                <p className="text-muted-foreground">
                  <strong className="text-foreground">How to compare:</strong> Select two versions
                  from the version dropdown, then run comparison.
                </p>
                <p className="text-muted-foreground">
                  <strong className="text-foreground">Results:</strong> AI analyzes what changed
                  and assesses the impact of each modification.
                </p>
              </div>
            </section>

            {/* Knowledge Base */}
            <section>
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Database className="h-5 w-5 text-blue-500" />
                Knowledge Base
              </h2>
              <p className="text-muted-foreground mb-4">
                Analysis is driven by Knowledge Base items accessible via the Contract Guidance tab.
                Each item tells the AI what to look for.
              </p>
              <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                <p className="font-medium text-sm">Each KB Item Contains:</p>
                <div className="space-y-2 text-xs">
                  <div className="flex gap-2">
                    <span className="font-medium text-foreground w-28 shrink-0">Question</span>
                    <span className="text-muted-foreground">What to look for in the contract</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="font-medium text-foreground w-28 shrink-0">Why It Matters</span>
                    <span className="text-muted-foreground">Risk implications and context</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="font-medium text-foreground w-28 shrink-0">What to Look For</span>
                    <span className="text-muted-foreground">Specific red flags and criteria</span>
                  </div>
                </div>
              </div>
            </section>

            {/* Quick Reference */}
            <section className="pb-4">
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-slate-500" />
                Quick Reference
              </h2>
              <div className="grid grid-cols-2 gap-4 text-xs">
                <div className="bg-muted/50 rounded-lg p-3">
                  <p className="font-medium mb-2">Actions</p>
                  <ul className="text-muted-foreground space-y-1">
                    <li>• <strong>Run</strong> — analyze the document</li>
                    <li>• <strong>View</strong> — see analysis results</li>
                    <li>• <strong>Download</strong> — get annotated PDF/DOCX</li>
                  </ul>
                </div>
                <div className="bg-muted/50 rounded-lg p-3">
                  <p className="font-medium mb-2">Risk Levels</p>
                  <ul className="text-muted-foreground space-y-1">
                    <li>• <strong>High</strong> — requires negotiation</li>
                    <li>• <strong>Medium</strong> — needs review</li>
                    <li>• <strong>Low</strong> — minor concern</li>
                  </ul>
                </div>
              </div>
            </section>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  )
}

// Helper components

interface StepProps {
  number: number
  title: string
  description: string
}

const Step: FC<StepProps> = ({ number, title, description }) => (
  <div className="flex gap-3">
    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium">
      {number}
    </div>
    <div className="flex-1">
      <p className="font-medium">{title}</p>
      <p className="text-muted-foreground text-xs mt-1">{description}</p>
    </div>
  </div>
)
